<script setup lang="ts">
import { computed, ref, watch, type Ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import RangeInput from '@/components/lib/RangeInput.vue'
import type { TFilterField } from '../../../../types/api/reports'
import NumberInput from '../../../lib/NumberInput.vue'
import type { TFilterEmitValue } from './utils'
import { getRangeFilterValues } from '../../../../store/api/gql/range_filter_values/queries'

interface Props {
  field: TFilterField
  isActive: boolean
  value: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:filterField': [TFilterEmitValue]
}>()

export interface RangeValue {
  id: string
  cssStyle: string
  cssClass: string
  percentage: number
}
const rangeValues: Ref<RangeValue[]> = ref([
  {
    id: '0',
    cssStyle: `--clr-input-back: var(--clr-black-3); --clr-input-handle: var(--clr-primary-2);`,
    cssClass: '',
    percentage: 0.5,
  },
  {
    id: '1',
    cssStyle: `--clr-input-back: var(--clr-black-3);`,
    cssClass: '',
    percentage: 0.5,
  },
])

const { data } = useQuery({
  queryKey: ['range_filter_values', props.field.id],
  queryFn: getRangeFilterValues(props.field.field_name),
  throwOnError: true,
})

const ranges = computed(
  () => data.value?.range_filter_values[props.field.field_name] || { min: 0, max: 666 },
)

const handleUpdate = (newValue: string) =>
  emit('update:filterField', {
    field: props.field,
    value: newValue,
    is_active: props.isActive,
  })

const handleRangeInput = ({ values }: { values: RangeValue[] }) => {
  const percentage = values[0].percentage
  const minMaxDiff = ranges.value.max - ranges.value.min
  const value = ranges.value.min + minMaxDiff * percentage
  handleUpdate(`${value.toFixed(2)}`)
}

watch(
  () => props.value,
  newValue => {
    const minMaxDiff = ranges.value.max - ranges.value.min
    const percentage = (Number(newValue) - ranges.value.min) / minMaxDiff
    rangeValues.value[0].percentage = percentage
    rangeValues.value[1].percentage = 1 - percentage
    rangeValues.value = [...rangeValues.value] // trigger reactivity
  },
  { immediate: true },
)
</script>
<template>
  <div
    class="f-column mh-0 mw-0"
    style="gap: var(--app-gap-2)"
  >
    <RangeInput
      :values="rangeValues"
      @values:update="handleRangeInput"
    />

    <NumberInput
      class="mr-auto"
      style="--input-width: clamp(1px, 8rem, 100%)"
      :value="Number(value)"
      :hasMinus="false"
      :hasPlus="false"
      :min="ranges.min"
      :max="ranges.max"
      @input="handleUpdate"
    />
  </div>
</template>
